import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import WaveSurfer from "wavesurfer.js"
import { colors as designSystemColors } from "../../../design_system/tokens.js"

export default class extends Controller {
  static values = {
    demoId: { type: Number, default: 0 },
    resourceId: Number,
    referrer: { type: String, default: "player" },
    locale: { type: String, default: "en" },
    waveColor: { type: String, default: "bluegreen.400" },
    progressColor: { type: String, default: "bluegreen.700" }
  }

  static targets = [
    "asset",
    "waveform",
    "playButton",
    "pauseButton",
    "currentTime",
    "duration",
    "unavailable"
  ]

  // Audio engagement tracking properties - using time ranges for accurate tracking
  listenedRanges = [] // Array of [start, end] time ranges
  currentRangeStart = null // Start time of current listening session
  lastRecordedTime = null // Last time we recorded

  connect() {
    if (!this.hasAssetTarget || !this.hasWaveformTarget) return

    this.initializeWaveSurfer()

    // Track listen time before page unload (external navigation, tab close)
    if (this.demoIdValue === 0) {
      this.beforeUnloadHandler = () => {
        this.trackListenTimeBeforeUnload()
      }
      window.addEventListener("beforeunload", this.beforeUnloadHandler)
      window.addEventListener("pagehide", this.beforeUnloadHandler)

      // Track listen time before Turbo navigation (back button, internal links)
      this.turboBeforeVisitHandler = () => {
        this.trackListenTimeBeforeUnload()
      }
      document.addEventListener("turbo:before-visit", this.turboBeforeVisitHandler)
      document.addEventListener("turbo:before-render", this.turboBeforeVisitHandler)
    }
  }

  disconnect() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler)
      window.removeEventListener("pagehide", this.beforeUnloadHandler)
    }

    if (this.turboBeforeVisitHandler) {
      document.removeEventListener("turbo:before-visit", this.turboBeforeVisitHandler)
      document.removeEventListener("turbo:before-render", this.turboBeforeVisitHandler)
    }

    if (this.wavesurfer) {
      this.wavesurfer.destroy()
      this.wavesurfer = null
    }
  }

  play(_event) {
    if (!this.wavesurfer) return
    this.wavesurfer.play()
    this.setPlayingState(true)
    this.logEvent("play")
    this.startTracking()
  }

  pause(_event) {
    if (!this.wavesurfer) return
    this.wavesurfer.pause()
    this.setPlayingState(false)
    this.logEvent("pause")
    this.stopTracking()
    this.trackListenTime()
  }

  rewind(_event) {
    if (!this.wavesurfer) return
    const currentTime = this.wavesurfer.getCurrentTime()
    const nextTime = Math.max(0, currentTime - this.#skipTime)
    this.wavesurfer.setTime(nextTime)
    this.currentTimeTarget.textContent = this.#formatTime(nextTime)
    this.logEvent("rewind")
    this.handleSeek()
  }

  fastForward(_event) {
    if (!this.wavesurfer) return
    const duration = this.wavesurfer.getDuration()
    if (!Number.isFinite(duration)) return
    const currentTime = this.wavesurfer.getCurrentTime()
    const nextTime = Math.min(duration, currentTime + this.#skipTime)
    this.wavesurfer.setTime(nextTime)
    this.currentTimeTarget.textContent = this.#formatTime(nextTime)
    this.logEvent("fast_forward")
    this.handleSeek()
  }

  handleEnded() {
    this.setPlayingState(false)
    this.logEvent("ended")
    this.stopTracking()
    this.trackListenTime()
  }

  handleError() {
    if (this.hasWaveformTarget) this.waveformTarget.classList.add("hidden")
    if (this.hasUnavailableTarget) this.unavailableTarget.classList.remove("hidden")
  }

  setPlayingState(isPlaying) {
    if (this.hasPlayButtonTarget) this.playButtonTarget.classList.toggle("hidden", isPlaying)
    if (this.hasPauseButtonTarget) this.pauseButtonTarget.classList.toggle("hidden", !isPlaying)
  }

  initializeWaveSurfer() {
    const waveColor = this.#resolveWaveColor(this.waveColorValue, "rgb(103 188 183)")
    const progressColor = this.#resolveWaveColor(this.progressColorValue, "rgb(42 83 80)")

    this.wavesurfer = WaveSurfer.create({
      container: this.waveformTarget,
      media: this.assetTarget,
      waveColor,
      progressColor,
      height: 48,
      barWidth: 8,
      barGap: 4,
      barRadius: 12,
      barAlign: "bottom",
      normalize: true,
      cursorWidth: 0,
      dragToSeek: true,
      interact: true
    })

    this.wavesurfer.on("ready", () => {
      const duration = this.wavesurfer.getDuration()
      this.durationTarget.textContent = this.#formatTime(duration)
      this.currentTimeTarget.textContent = this.#formatTime(0)
    })

    this.wavesurfer.on("timeupdate", (time) => {
      this.currentTimeTarget.textContent = this.#formatTime(time)

      // Update listened time tracking (only for non-demo audio)
      if (this.demoIdValue === 0) {
        this.updateListenedTime()
      }
    })

    this.wavesurfer.on("interaction", () => {
      this.logEvent("seek")
      this.handleSeek()
    })

    this.wavesurfer.on("finish", () => {
      this.handleEnded()
    })

    this.wavesurfer.on("error", () => {
      this.handleError()
    })
  }

  //private
  #skipTime = 15

  #resolveWaveColor(colorValue, fallback) {
    if (!colorValue) return fallback

    const normalizedColorValue = colorValue.trim()

    // Dotted "hue.step" path (e.g. "bluegreen.400") — look up the RGB-channel
    // triplet from tokens.js color scales and wrap in rgb() so canvas accepts it.
    // Token values are stored as space-separated R G B so Tailwind's
    // <alpha-value> placeholder works (see design_system/tokens.css).
    if (normalizedColorValue.includes(".")) {
      const [hue, step] = normalizedColorValue.split(".")
      const triplet = designSystemColors[hue]?.[step]
      if (triplet) return `rgb(${triplet})`
    }

    // Flat lookup (neutrals like "ink", "cream") returns a hex string directly.
    return designSystemColors[normalizedColorValue] || normalizedColorValue || fallback
  }

  #formatTime(currentTime) {
    if (!Number.isFinite(currentTime)) return "--"

    const minutes = Math.floor(currentTime / 60)
    const seconds = Math.floor(currentTime % 60)

    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes
    const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds

    return `${formattedMinutes}:${formattedSeconds}`
  }

  async logEvent(eventType) {
    const payload = {
      event: `audio.${eventType}`,
      time: this.assetTarget.currentTime,
      resource_id: this.resourceIdValue,
      referrer: this.referrerValue,
      locale: this.localeValue
    }

    // If no demoId, log to user_activities
    if (this.demoIdValue !== 0) {
      await post(`/demos/${this.demoIdValue}/activity`, {
        body: JSON.stringify(payload)
      })
    } else {
      await post("/user_activities/activity", {
        body: JSON.stringify(payload)
      })
    }
  }

  startTracking() {
    if (this.demoIdValue !== 0 || !this.assetTarget) return
    const currentTime = this.assetTarget.currentTime
    this.currentRangeStart = currentTime
    this.lastRecordedTime = currentTime
  }

  stopTracking() {
    if (this.demoIdValue !== 0 || this.currentRangeStart === null || !this.assetTarget) return
    const currentTime = this.assetTarget.currentTime

    // Only add range if there was actual playback
    if (currentTime > this.currentRangeStart) {
      this.listenedRanges.push([this.currentRangeStart, currentTime])
    }

    this.currentRangeStart = null
    this.lastRecordedTime = null
  }

  handleSeek() {
    if (this.demoIdValue !== 0 || !this.assetTarget) return

    const currentTime = this.assetTarget.currentTime

    // If we were tracking, close the current range
    if (this.currentRangeStart !== null && this.lastRecordedTime !== null) {
      // Only add if we listened to something before seeking
      if (this.lastRecordedTime > this.currentRangeStart) {
        this.listenedRanges.push([this.currentRangeStart, this.lastRecordedTime])
      }
    }

    // Start new range at the seeked position if playing
    if (!this.assetTarget.paused) {
      this.currentRangeStart = currentTime
      this.lastRecordedTime = currentTime
    } else {
      this.currentRangeStart = null
      this.lastRecordedTime = null
    }
  }

  updateListenedTime() {
    if (this.demoIdValue !== 0 || this.currentRangeStart === null || !this.assetTarget) return

    const currentTime = this.assetTarget.currentTime

    // Detect discontinuities (like seeking during playback without triggering seeked event)
    if (this.lastRecordedTime !== null && Math.abs(currentTime - this.lastRecordedTime) > 1.5) {
      // Close current range and start new one
      if (this.lastRecordedTime > this.currentRangeStart) {
        this.listenedRanges.push([this.currentRangeStart, this.lastRecordedTime])
      }
      this.currentRangeStart = currentTime
    }

    this.lastRecordedTime = currentTime
  }

  async trackListenTime() {
    // Only track listen time for non-demo audio (UserActivity, not DemoActivity)
    if (this.demoIdValue === 0 && this.listenedRanges.length > 0 && this.assetTarget) {
      const audioDuration = this.assetTarget.duration

      const payload = {
        event: "audio.listen_time",
        resource_id: this.resourceIdValue,
        referrer: this.referrerValue,
        locale: this.localeValue,
        listened_ranges: this.listenedRanges,
        audio_duration_seconds: Math.round(audioDuration * 100) / 100,
        current_time: Math.round(this.assetTarget.currentTime * 100) / 100
      }

      await post("/user_activities/activity", {
        body: JSON.stringify(payload)
      })

      // Clear ranges after successful send to avoid duplicate tracking
      this.listenedRanges = []
      this.currentRangeStart = null
      this.lastRecordedTime = null
    }
  }

  trackListenTimeBeforeUnload() {
    // Use sendBeacon for reliable delivery during page unload
    if (this.demoIdValue !== 0 || !this.assetTarget) return

    // Important: Stop tracking to capture current listening session
    // This adds the current range (if playing) to listenedRanges
    this.stopTracking()

    // Only send if there are ranges to track
    if (this.listenedRanges.length > 0) {
      const audioDuration = this.assetTarget.duration

      const payload = {
        event: "audio.listen_time",
        resource_id: this.resourceIdValue,
        referrer: this.referrerValue,
        locale: this.localeValue,
        listened_ranges: this.listenedRanges,
        audio_duration_seconds: Math.round(audioDuration * 100) / 100,
        current_time: Math.round(this.assetTarget.currentTime * 100) / 100
      }

      // sendBeacon requires a Blob with proper content type
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" })
      navigator.sendBeacon("/user_activities/activity", blob)

      // Clear ranges after sending to avoid duplicate tracking
      this.listenedRanges = []
      this.currentRangeStart = null
      this.lastRecordedTime = null
    }
  }
}
